import { css } from 'aphrodite';
import { UnregisterCallback } from 'history';
import * as React from 'react';
import { NavLink as RouterLink, useHistory } from 'react-router-dom';
import { useTextMessagingOptional, useUserSession } from '../../../models/hooks/appStateHooks';
import { useAutomationsOnHoldQuery } from '../../../queries';
import { brandPrimary } from '../../styles/colors';
import { INavBarLinkBase, NavBarLink } from '../Happiness/NavBarLink';
import { CompactLogoIcon } from '../svgs/icons/CompactLogoIcon';
import { FlagIcon } from '../svgs/icons/FlagIcon';
import { LogoIcon } from '../svgs/icons/LogoIcon';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	compactLayout?: boolean;
	header?: React.ReactNode;
	links?: INavBarLinkBase[];
	onRenderLogo?(compactLayout?: boolean): React.ReactNode;
}

export function NavBar({ className, compactLayout, header, links, onRenderLogo }: IProps) {
	const [automationsPending, setAutomationsPending] = React.useState(false);
	const userSession = useUserSession();
	const textMessaging = useTextMessagingOptional();
	const history = useHistory();

	const automationsEnabled = userSession?.account?.features?.automation?.enabled ?? false;

	const { data: automationsOnHoldData } = useAutomationsOnHoldQuery({
		includeAll: userSession?.canSendOnBehalf,
		enabled: automationsEnabled,
	});

	const checkForAutomations = React.useCallback(() => {
		if (automationsEnabled && automationsOnHoldData) {
			const automationsWithContacts = automationsOnHoldData.filter(x => x.count > 0);
			setAutomationsPending(automationsWithContacts.length > 0);
		}
	}, [automationsEnabled, automationsOnHoldData]);

	React.useEffect(() => {
		let unregisterHandle: UnregisterCallback = null;
		unregisterHandle = history.listen(() => checkForAutomations());
		checkForAutomations();
		return () => {
			if (unregisterHandle) {
				unregisterHandle();
			}
		};
	}, [checkForAutomations]);

	const getAccessory = (linkProps: INavBarLinkBase): JSX.Element => {
		switch (linkProps.linkName) {
			case 'Automations': {
				if (automationsPending) {
					return <FlagIcon />;
				}
				return null;
			}
			case 'Texting': {
				if (textMessaging?.totalUnreadMessages) {
					return (
						<div
							className={css(
								compactLayout ? styleSheet.compactUnreadTextMessageCount : styleSheet.unreadTextMessageCount
							)}
						>
							{textMessaging.totalUnreadMessages}
						</div>
					);
				}
				return null;
			}
			default: {
				return null;
			}
		}
	};

	return (
		<nav className={`nav-bar ${css(styleSheet.nav)} ${className || ''}`}>
			{header || (
				<div className={css(styleSheet.header)}>
					{onRenderLogo?.(compactLayout) || (
						<RouterLink to='/dashboard'>
							{compactLayout ? <CompactLogoIcon /> : <LogoIcon textColor={brandPrimary} />}
						</RouterLink>
					)}
				</div>
			)}
			<div className={css(styleSheet.links)}>
				<div>
					{!!links &&
						links.map(linkProps => {
							return (
								<NavBarLink
									{...linkProps}
									accessory={getAccessory(linkProps)}
									compactLayout={compactLayout}
									key={typeof linkProps.path === 'object' ? linkProps.path[0] : linkProps.path}
									activeLinkStyles={[styleSheet.linkActive, compactLayout && styleSheet.compactLinkPadding]}
									linkStyles={[styleSheet.link, compactLayout && styleSheet.compactLinkPadding]}
								/>
							);
						})}
				</div>
			</div>
		</nav>
	);
}
